import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import Page from '@/components/Page';
import Card from '@/components/Card';
import Stack from '@/components/Stack';
import Button from '@/components/Button';
import Alert from '@/components/Alert';
import LoadingState from '@/components/LoadingState';
import { Body } from '@/components/Typography';
import ConfirmationModal from '@/components/ConfirmationModal';
import { AssessmentsAPI, AssessmentHealthRow } from '@/services/assessmentsAPI';
import HealthTable from './HealthTable';

const STATUS_OPTIONS = [
  { value: '', label: 'In flight & stuck' },
  { value: 'scoring', label: 'Scoring' },
  { value: 'transcribing', label: 'Transcribing' },
  { value: 'recording', label: 'Recording' },
  { value: 'pending', label: 'Pending' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
];

const AssessmentHealth: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const status = new URLSearchParams(location.search).get('status') || '';

  const [rows, setRows] = useState<AssessmentHealthRow[]>([]);
  const [meta, setMeta] = useState<{
    stale_minutes: number;
    counts_by_status: Record<string, number>;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmRow, setConfirmRow] = useState<AssessmentHealthRow | null>(
    null,
  );
  const [busyExternalId, setBusyExternalId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await AssessmentsAPI.getAssessmentHealth({
        status: status || undefined,
        stale_minutes: 30,
      });
      setRows(res.data);
      setMeta(res.meta);
    } catch (e) {
      console.error('[AssessmentHealth] Error:', e);
      setError('Failed to load assessment health.');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const loadRef = useRef(load);
  useEffect(() => {
    loadRef.current = load;
  }, [load]);

  const rerunTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(
    () => () => {
      if (rerunTimeoutRef.current) clearTimeout(rerunTimeoutRef.current);
    },
    [],
  );

  const handleStatusChange = (value: string) => {
    const params = new URLSearchParams(location.search);
    if (value) params.set('status', value);
    else params.delete('status');
    navigate(`${location.pathname}?${params.toString()}`);
  };

  const runRerun = async (row: AssessmentHealthRow) => {
    setBusyExternalId(row.external_id);
    try {
      await AssessmentsAPI.rerunScoring(row.external_id);
      await load();
      rerunTimeoutRef.current = setTimeout(() => loadRef.current(), 6000);
    } finally {
      setBusyExternalId(null);
    }
  };

  return (
    <Page title="Assessment Health" size="fullWidth" headerPadding>
      <Card changeStyle={{ padding: 16 }}>
        <Stack vertical gap={16}>
          <Stack gap={12} align="center" justify="apart" wrap>
            <select
              value={status}
              onChange={(e) => handleStatusChange(e.target.value)}
              disabled={loading}
              style={{
                padding: '6px 12px',
                border: '1px solid #ddd',
                borderRadius: 4,
              }}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <Button
              onClick={load}
              a11yLabel="Refresh"
              label="Refresh"
              disabled={loading}
            />
          </Stack>

          {meta && (
            <Body>
              {rows.length} assessment(s) · stuck threshold {meta.stale_minutes}
              m
            </Body>
          )}

          {loading ? (
            <LoadingState text="Loading assessment health..." />
          ) : error ? (
            <Stack vertical gap={12} align="center" css={{ padding: 24 }}>
              <Alert
                status="danger"
                description={error}
                icon={faExclamationTriangle}
              />
              <Button onClick={load} a11yLabel="Retry" label="Retry" />
            </Stack>
          ) : rows.length === 0 ? (
            <Body>No assessments found for this filter.</Body>
          ) : (
            <HealthTable
              rows={rows}
              onRerun={setConfirmRow}
              busyExternalId={busyExternalId}
            />
          )}
        </Stack>
      </Card>

      {confirmRow && (
        <ConfirmationModal
          title="Re-run scoring"
          bodyContentText={`Re-run scoring for ${confirmRow.worker_name || `user ${confirmRow.user_id}`} (${confirmRow.skill_name || confirmRow.skill_id})? This re-queues the assessment; the list refreshes shortly.`}
          acceptButtonLabel="Re-run scoring"
          denyButtonLabel="Cancel"
          acceptAction={async () => {
            await runRerun(confirmRow);
          }}
          denyAction={() => {}}
          hideModal={() => setConfirmRow(null)}
        />
      )}
    </Page>
  );
};

export default AssessmentHealth;
